import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LEGAL_CONTENT } from '@/legal/content';
import { LEGAL_CONFIG } from '@/legal/config';
import { ThemeToggle } from '@/components/ThemeToggle';
import AppFooter from '@/components/AppFooter';
import RUOBadge from '@/components/RUOBadge';
import SeoHead from '@/components/SeoHead';
import InkPhDLogo from '@/components/InkPhDLogo';

/**
 * Universal legal page renderer.
 * Renders one of the 4 legal pages: terms / privacy / ruo / ai
 * with a BG/EN language toggle and consistent layout.
 */
export default function LegalPage({ kind }) {
  const [lang, setLang] = useState('bg');
  const navigate = useNavigate();

  const content = LEGAL_CONTENT[kind]?.[lang];
  if (!content) {
    return <div className="p-8 text-center text-muted-foreground">Page not found.</div>;
  }

  const PATHS = { terms: '/legal/terms', privacy: '/legal/privacy', ruo: '/legal/ruo',
    ai: '/legal/ai-transparency', cookies: '/legal/cookies', aiact: '/legal/ai-act',
    dpa: '/legal/dpa', subprocessors: '/legal/subprocessors', billing: '/legal/billing' };
  const en = LEGAL_CONTENT[kind]?.en || content;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SeoHead title={en.title} description={`${en.title} — ${en.subtitle}`}
        path={PATHS[kind] || '/'} />
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="legal-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> {lang === 'en' ? 'Back' : 'Назад'}
          </button>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="legal-home-link"><InkPhDLogo size="sm" /></Link>
            <RUOBadge lang={lang} />
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex border border-border rounded-md overflow-hidden text-xs">
              <button
                onClick={() => setLang('bg')}
                className={`px-2 py-1 ${lang === 'bg' ? 'bg-teal-700 text-white' : 'hover:bg-muted'}`}
                data-testid="legal-lang-bg"
              >BG</button>
              <button
                onClick={() => setLang('en')}
                className={`px-2 py-1 ${lang === 'en' ? 'bg-teal-700 text-white' : 'hover:bg-muted'}`}
                data-testid="legal-lang-en"
              >EN</button>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-12" data-testid={`legal-page-${kind}`}>
        <h1 className="text-4xl font-serif font-bold tracking-tight mb-1">{content.title}</h1>
        <p className="text-sm text-muted-foreground mb-10">{content.subtitle}</p>

        <div className="prose prose-sm dark:prose-invert max-w-none space-y-8">
          {content.sections.map((section, idx) => (
            <section key={idx} data-testid={`legal-section-${idx}`}>
              <h2 className="text-lg font-serif font-semibold mb-2 text-teal-800 dark:text-teal-300">{section.h}</h2>
              <p className="text-sm leading-relaxed text-foreground/85">{section.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-border text-xs text-muted-foreground space-y-1">
          <p>{lang === 'en' ? 'Operator / Data controller' : 'Оператор / Контролер на данните'}: {LEGAL_CONFIG.controller.legal_name}</p>
          <p>{lang === 'en' ? 'Jurisdictions covered' : 'Покрити юрисдикции'}: {LEGAL_CONFIG.jurisdictions.primary} · {LEGAL_CONFIG.jurisdictions.secondary.join(' · ')}</p>
        </div>

        <div className="mt-8">
          <Button onClick={() => navigate(-1)} variant="outline" size="sm" data-testid="legal-bottom-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {lang === 'en' ? 'Back to app' : 'Обратно към приложението'}
          </Button>
        </div>
      </main>

      <AppFooter lang={lang} />
    </div>
  );
}
